/** The Activity tab (plan §1.1, §4.4): the change history of the booking, its guest and
 *  each party member's booking — one `HistoryPanel` per record, each grouping the rows of
 *  one save by correlation — then the notifications delivered for the booking, and the
 *  grids where the rest of its trail lives.
 *
 *  The history needs console_audit_view (the changes API's permission). A party member's
 *  history mounts when its disclosure opens: a party of 30 is 30 requests otherwise. The
 *  time axis's dots land here through `intent`: a change on the history, a delivery on its
 *  row (tinted). */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, Collapse, Link, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';

import type { BookingRecordEditContext } from '@/api/bookingRecord';
import * as bookingsApi from '@/api/bookings';
import type { BookingDetail } from '@/api/bookings';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { usePermissions } from '@/hooks/usePermissions';
import { auditHref, cond, importRunsHref } from '@/utils/gridLinks';

import { deliveryLogsOfBookingHref, type DeliveryLogCtx } from '../../overview/bookingLinks';
import { DeliveryLogsGrid } from '../../overview/DeliveryLogsGrid';

import { TabSection, useSectionIntent, type SectionIntent } from './TabSection';
import { TabSkeleton } from './TabSkeleton';

/** The objchange content types of the records a booking save writes. */
export const BOOKING_CONTENT_TYPE = 'demo.group_tour_user';
export const USER_CONTENT_TYPE = 'users.user';

function personName(u: { first_name?: string | null; last_name?: string | null } | null | undefined): string {
  return [u?.first_name, u?.last_name].filter(Boolean).join(' ');
}

function HistoryBlock({ title, note, children }: { title: string; note?: string | null; children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gap: 0.5 }}>
      <Typography variant="caption" component="h3" color="text.secondary" sx={{ m: 0, fontWeight: 600 }}>
        {title}
        {note && (
          <Box component="span" sx={{ fontWeight: 400 }}>
            {' '}
            · {note}
          </Box>
        )}
      </Typography>
      {children}
    </Box>
  );
}

function PartyHistory({ bookingId, name }: { bookingId: number; name: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <Box>
      <Button
        size="small"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={id}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ px: 0.75 }}
      >
        Party: {name} (#{bookingId})
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box id={id} sx={{ pt: 0.5 }}>
          <HistoryPanel contentType={BOOKING_CONTENT_TYPE} objectPk={String(bookingId)} />
        </Box>
      </Collapse>
    </Box>
  );
}

export const HISTORY_SECTION_ID = 'booking-activity-history';
export const DELIVERIES_SECTION_ID = 'booking-activity-deliveries';
const SECTION_IDS = { history: HISTORY_SECTION_ID, deliveries: DELIVERIES_SECTION_ID };

/** Where the time axis sends the operator on this tab: a change → the change history; a
 *  delivery → its row in Deliveries. */
export type ActivityIntent = SectionIntent<'history' | 'deliveries'>;

const deliveriesQuery = (bookingId: number) => ({
  queryKey: ['bookings', 'delivery', bookingId],
  queryFn: () => bookingsApi.listDeliveryLogs(bookingId),
});

function Deliveries({ bookingId, ctx, highlightId }: { bookingId: number; ctx: DeliveryLogCtx; highlightId: string | null }) {
  const { data, isLoading, error } = useQuery(deliveriesQuery(bookingId));
  if (isLoading) return <TabSkeleton label="Loading delivery logs" />;
  if (error || !data) return <Alert severity="error">Couldn’t load delivery logs.</Alert>;
  return <DeliveryLogsGrid logs={data} ctx={ctx} highlightId={highlightId} />;
}

export function ActivityTab({
  bookingId,
  detail,
  editContext,
  intent = null,
  onIntentDone,
}: {
  bookingId: number;
  detail: BookingDetail;
  editContext: BookingRecordEditContext;
  intent?: ActivityIntent | null;
  onIntentDone?: () => void;
}) {
  const { hasPerm } = usePermissions();
  const canViewHistory = hasPerm('console_audit_view');
  const tenantId = detail.tenant?.id ?? editContext.tenant.id;
  const ctx = useMemo<DeliveryLogCtx>(() => ({ bookingId, tenantId }), [bookingId, tenantId]);
  const guest = editContext.guest;
  const guestEmail = detail.guest?.email ?? null;
  const affects = guest?.affects_bookings ?? 0;

  // The deliveries sit under the history: scroll once they are there (a row's position
  // depends on them too).
  const deliveriesSettled = !useQuery(deliveriesQuery(bookingId)).isLoading;
  useSectionIntent(intent, SECTION_IDS, deliveriesSettled, onIntentDone);
  const [highlight, setHighlight] = useState<string | null>(null);
  const pointed = intent?.section === 'deliveries' ? (intent.highlight ?? null) : undefined;
  useEffect(() => {
    if (pointed !== undefined) setHighlight(pointed);
  }, [pointed, intent?.seq]);

  const elsewhere = [
    guestEmail ? { label: 'Console audit log for this guest', href: auditHref({ target_email: guestEmail }) } : null,
    {
      label: 'Import runs that touched this booking',
      href: importRunsHref([cond('participant', String(bookingId), 'exact')], tenantId),
    },
  ].filter((l): l is { label: string; href: string } => l !== null);

  return (
    <Box>
      <TabSection id={HISTORY_SECTION_ID} title="Change history">
        {canViewHistory ? (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <HistoryBlock title={`Booking #${bookingId}`}>
              <HistoryPanel contentType={BOOKING_CONTENT_TYPE} objectPk={String(bookingId)} />
            </HistoryBlock>
            {guest && (
              <HistoryBlock
                title={`Guest ${guest.display_name || personName(detail.guest) || `#${guest.user_id}`}`}
                note={affects > 1 ? `the guest's profile, across all ${affects} bookings` : null}
              >
                <HistoryPanel contentType={USER_CONTENT_TYPE} objectPk={String(guest.user_id)} />
              </HistoryBlock>
            )}
            {editContext.party.length > 0 && (
              <HistoryBlock title="Party" note={`${editContext.party.length} ${editContext.party.length === 1 ? 'member' : 'members'}`}>
                <Stack spacing={0.25} alignItems="flex-start">
                  {editContext.party.map((m) => (
                    <PartyHistory
                      key={m.booking_id}
                      bookingId={m.booking_id}
                      name={personName(m.user) || `Booking #${m.booking_id}`}
                    />
                  ))}
                </Stack>
              </HistoryBlock>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Change history needs the console_audit_view permission.
          </Typography>
        )}
      </TabSection>

      <TabSection
        id={DELIVERIES_SECTION_ID}
        title="Deliveries"
        action={
          <Button
            size="small"
            variant="outlined"
            component="a"
            href={deliveryLogsOfBookingHref(ctx)}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon fontSize="small" />}
          >
            Open in Delivery Logs
          </Button>
        }
      >
        <Deliveries bookingId={bookingId} ctx={ctx} highlightId={highlight} />
      </TabSection>

      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ typography: 'body2' }} data-activity-links>
        {elsewhere.map((l) => (
          <Link key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" underline="hover">
            {l.label}
          </Link>
        ))}
      </Stack>
    </Box>
  );
}
